import moment from "moment";
import {
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import CADLabel from "@components/cad-labels";
import TableDate from "@components/table-date";
import { CADIncident } from "@models/cad";
import { useHeight } from "@hooks/useHeight";
import { getURL, urls } from "@utils/routing";
import MaterialTable, { Column } from "@material-table/core";
import classes from "./cad-incidents.module.css";
import { CADIncidentsTab } from "@models/routing";
import { Tooltip, Typography } from "@mui/material";
import { SearchBox } from "@components/search-box/search-box";
import { useHistoryState } from "@hooks/useHistoryState";

interface CADIncidentsListProps {
  CADIncidents: CADIncident[]
  tableTitle: CADIncidentsTab
  tableNoDataMessage?: string
  isLoading?: boolean
  displayTooltipsScore?: boolean
}

export default function CADIncidentsTable({
  CADIncidents,
  tableTitle,
  tableNoDataMessage,
  isLoading,
  displayTooltipsScore = false,
}: CADIncidentsListProps) {
  const [searchTable, setSearchTable] = useHistoryState("material-table-search", "");
  const navigate = useNavigate();
  const height = useHeight();
  const [columns] = useState<Array<Column<CADIncident>>>([
    {
      title: "Incident Date Time",
      field: "EntryDateTime",
      width: "15%",
      render: ({ EntryDateTime }) => <TableDate date={EntryDateTime} />,
      sorting: true,
      customSort: (a, b) => (moment(b.EntryDateTime).unix() ?? 0) - (moment(a.EntryDateTime).unix() ?? 0),
    },
    {
      title: "Incident",
      field: "AgencyIncidentCallTypeDescription",
      render: (item) => (
        <Tooltip title={displayTooltipsScore ? item.scoreDetails?.value : ""} placement="left">
          <div>
            <CADLabel
              incidentCallTypeDescription={item.AgencyIncidentCallTypeDescription ?? ""}
              incidentNumberDisplay={item.IncidentNumberDisplay ?? ""}
              incidentId={item.id ?? ""}
              address={item.full_address ?? ""}
              units={item.units ?? []}
              rts={item.rts}
              extended={false}
              simulation={item.simulation}
              sharedTo={item.sharedTo}
              sharedSource={item.sharedSource}
            />
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Last Updated",
      field: "modified_unix_date",
      width: "15%",
      render: (item) => moment((item.modified_unix_date ?? 0) * 1000).fromNow(),
      sorting: true,
      customSort: (a, b) => (b.modified_unix_date ?? 0) - (a.modified_unix_date ?? 0),
    },
    {
      title: "Incident Status",
      field: "ClosedDateTime",
      width: "15%",
      hidden: tableTitle !== CADIncidentsTab.Search,
      render: (item) => (
        <Typography className={classes.cad_incidents_status_label} style={{ backgroundColor: item.ClosedDateTime ? "#d39a9c" : "#72918d" }}>
          {item.ClosedDateTime
            ? "Inactive"
            : "Active"}
        </Typography>
      ),
      sorting: true,
      customSort: (a, b) => {
        const aClosedDateTime = a.ClosedDateTime ? moment(a.ClosedDateTime).unix() : moment().unix();
        const bClosedDateTime = b.ClosedDateTime ? moment(b.ClosedDateTime).unix() : moment().unix();
        return bClosedDateTime - aClosedDateTime;
      },
    },
  ]);

  const tableRenderTitle = () => {
    switch (tableTitle) {
      case CADIncidentsTab.Active:
        return "Active CAD Incidents";
      case CADIncidentsTab.Inactive:
        return "Inactive CAD Incidents";
      case CADIncidentsTab.Search:
      default:
        return "Incidents";
    }
  };

  const tableData = useMemo(() => {
    if (!searchTable) {
      return CADIncidents;
    }
    return CADIncidents.filter((item) => {
      const searchString = [
        item.AgencyIncidentCallTypeDescription,
        item.full_address,
        item.IncidentNumber,
        item.ReportNumber,
        (item.units ?? []).map((unit) => unit.UnitID).toString(),
      ].join(" ").toLowerCase();
      return searchString.includes(searchTable.toLowerCase());
    });
  }, [CADIncidents, searchTable]);

  return (
    <div className="material-table-core-wrapper">
      <MaterialTable
        title={(
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" style={{ color: "white" }}>{tableRenderTitle()}</Typography>
            <SearchBox
              containerStyle={{ marginRight: 10 }}
              inputProps={{
                value: searchTable,
                fullWidth: true,
                onChange: (e) => setSearchTable(e.target.value),
              }}
            />
          </div>
        )}
        data={tableData}
        columns={columns.map((c) => ({
          ...c,
        }))}
        localization={{
          body: {
            emptyDataSourceMessage: tableNoDataMessage || "No incidents to display",
          },
        }}
        options={{
          paging: false,
          sorting: true,
          maxBodyHeight: height - 90,
          headerStyle: {
            position: "sticky",
            top: "0",
          },
          rowStyle: (data: CADIncident) => ({
            backgroundColor: (data.sharedSource && data.sharedSource.isExternal) ? "#fadea6" : data.simulation ? "#b8e4fc" : "",
          }),
        }}
        isLoading={isLoading}
        onRowClick={(_event, rowData) => navigate(`${getURL(urls.routingAppPrefix)}/${getURL(urls.adminCADIncidentDetails, { incidentId: rowData?.id || rowData?._id })}`)}
      />
    </div>
  );
}
